import Modalui from "src/components/UI/modal/Modalui";
import styles from "./onBoarding.module.scss";
import { useState, useEffect, useCallback } from "react";
import { spendAbleLogo } from "src/components/nav/logo";
import poweredBy from "src/assets/images/poweredBy.png";
import VerticalTimeline from "src/components/UI/verticalTimeline/VerticalTimeline";
import AddOrgModal from "src/components/onBoardingModals/addOrg/AddOrgModal";
import { useAppDispatch, useAppSelector } from "../store";
import { useNavigate, useSearchParams } from "react-router-dom";
import AddUserModal from "src/components/onBoardingModals/addUser/AddUserModal";
import { CORPORATE, INDIVIDUAL, REFERER, PARTICIPANT } from "src/components/constant/constant";
import AddWalletModal from "src/components/onBoardingModals/addWallet/AddWalletModal";
import OnBoardingSuccess from "src/components/onBoardingComponent/OnBoardingSuccessCard";
import OrganisationCreated from "src/assets/images/success1.png";
import WalletSuccess from "src/assets/images/success2.png";
import ParticipantSuccess from "src/assets/images/ParticipantSuccess.png";
import OnBoardComplete from "src/assets/images/CompleteOnboarding.png";
import { logScreenView, logUserInteraction, refreshSession, setUserDetail } from "src/utils/utils";
import { CorporateTimelineSteps, IndividualTimelineSteps, RefererTimelineSteps } from "./onBoardConfig";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ExitToAppOutlinedIcon from "@mui/icons-material/ExitToAppOutlined";
import { fetchConfigAction, logout, switchOrganisationAction } from "../auth/loginSlice";
import { Tooltip } from "@mui/material";
import SubscriptionModal from "src/components/orgListCard/addOrgModal/SubscriptionModal";
import { addWalletAction, fetchListSubscription, getSubscriptionAction } from "./onBoardingSlice";
import OrderCardModal from "src/components/onBoardingModals/orderCardModal/OrderCardModal";
import individual from "src/assets/images/individual.png";
import corporate from "src/assets/images/corporate.png";
import referral from "src/assets/images/referral.png";
import { CognitoAccessToken } from "amazon-cognito-identity-js";
import { ORG_ID, ORG_TYPE } from "src/components/constant/queryConstants";

export default function OnBoarding() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userInfo, cognitoRefreshToken, cognitoAccessToken, cognitoConfig } = useAppSelector((state) => state.auth);
  const {
    userDetail,
    onboardingLoading,
    userLoading,
    walletLoading,
    onboardingProgress,
    clickCounterProgress,
    subscriptionLimits
  } = useAppSelector((state) => state.onBoarding);
  const [open, setOpen] = useState<boolean>(true);
  const [completeOnBoarding, setCompleteOnBoarding] = useState<boolean>(false);
  const [onBoarding, setOnBoarding] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [nextClickCounter, setNextClickCounter] = useState(0);
  const [currentNode, setCurrentNode] = useState(0);
  const [currentSubsection, setCurrentSubsection] = useState(0);
  const [timelineSteps, setTimelineSteps] = useState(CorporateTimelineSteps);
  const [searchParams, setSearchParams] = useSearchParams();
  const organisationType = searchParams.get(ORG_TYPE);
  const organisationId = searchParams.get(ORG_ID);
  const initialFillLevel =
    timelineSteps[0]?.subsections?.length > 0 ? 100 / (timelineSteps[0].subsections.length + 1) : 100;
  const [fillLevels, setFillLevels] = useState(
    new Array(timelineSteps.length).fill(0).map((_, index) => (index === 0 ? initialFillLevel : 0))
  );
  const handleNextClick = () => {
    const subsections = timelineSteps[currentNode]?.subsections || [];

    if (subsections.length > 0 && currentSubsection < subsections.length - 1) {
      setCurrentSubsection(currentSubsection + 1);
      const fillPercentage = ((currentSubsection + 2) / (subsections.length + 1)) * 100;

      setFillLevels((prev) => prev.map((level, index) => (index === currentNode ? fillPercentage : level)));
    } else {
      if (currentNode < timelineSteps.length - 1) {
        setCurrentNode(currentNode + 1);
        setCurrentSubsection(-1);
        setFillLevels((prev) =>
          prev.map((level, index) => (index === currentNode ? 100 : index === currentNode + 1 ? level : level))
        );
      } else {
        setFillLevels((prev) => prev.map((level, index) => (index === currentNode ? 100 : level)));
      }
    }
  };

  useEffect(() => {
    if (cognitoAccessToken === null || cognitoAccessToken === undefined) {
      navigate("/sign-in", { replace: true });
    } else {
      const payload = new CognitoAccessToken({ AccessToken: cognitoAccessToken }).decodePayload();

      if (payload.current_organisation) {
        dispatch(fetchConfigAction());
      }
    }
  }, [cognitoAccessToken, dispatch, navigate]);

  useEffect(() => {
    if (cognitoConfig?.currentOrganisation?.organisation_id === organisationId) {
      dispatch(fetchListSubscription(cognitoConfig?.currentOrganisation?.organisation_id));
    }
  }, [dispatch, cognitoConfig?.currentOrganisation?.organisation_id, organisationId]);

  useEffect(() => {
    if (organisationType) {
      let selectedTimelineSteps;

      switch (organisationType) {
        case INDIVIDUAL:
          selectedTimelineSteps = IndividualTimelineSteps;
          break;
        case REFERER:
          selectedTimelineSteps = RefererTimelineSteps;
          break;
        case CORPORATE:
        default:
          selectedTimelineSteps = CorporateTimelineSteps;
          break;
      }
      setTimelineSteps(selectedTimelineSteps);
    }
  }, [organisationType]);

  useEffect(() => {
    if (userInfo) {
      setUserDetail(userInfo, undefined, () => {
        logScreenView("OnBoarding");
      });
    }
  }, [userInfo]);

  useEffect(() => {
    if (onboardingProgress === "addParticipant") {
      setOpen(false);
      setOnBoarding(true);
      setSelectedOption("SubscriptionSuccess");
      setNextClickCounter(clickCounterProgress);
    }
  }, [onboardingProgress]);

  useEffect(() => {
    if (organisationType) {
      setOpen(false);
      setOnBoarding(true);
      switch (organisationType) {
        case INDIVIDUAL:
          setSelectedOption("createParticipantOrganization");
          break;
        case CORPORATE:
          setSelectedOption("createOrganization");
          break;
        case REFERER:
          setSelectedOption("createReferrerOrganization");
      }
      setNextClickCounter(0);
    }
  }, [organisationType]);

  useEffect(() => {
    if (
      cognitoConfig?.organisationSubscriptions &&
      cognitoConfig?.organisationSubscriptions?.length > 0 &&
      userInfo?.role &&
      organisationId
    ) {
      setOpen(false);
      setOnBoarding(true);
      setSelectedOption("SubscriptionSuccess");
      setNextClickCounter(3);
    }
  }, [userInfo?.role]);

  const getSubscriptionDetail = useCallback(
    (subscriptionId: string) => {
      dispatch(getSubscriptionAction(subscriptionId));
    },
    [dispatch]
  );

  const switchForm = () => {
    switch (selectedOption) {
      case "createOrganization":
      case "createParticipantOrganization":
      case "createReferrerOrganization":
        setSelectedOption("OrganisationSuccess");
        break;
      case "OrganisationSuccess":
        setSelectedOption("SubscriptionModal");
        logScreenView("select_subscription");
        break;
      case "SubscriptionModal":
        setSelectedOption("SubscriptionSuccess");
        break;
      case "SubscriptionSuccess":
        setSelectedOption("addParticipant");
        logScreenView("add_user");
        break;
      case "addParticipant":
        setSelectedOption("ParticpantSuccess");
        break;
      case "ParticpantSuccess":
        setSelectedOption("addWallet");
        logScreenView("add_wallet");
        break;
      case "addWallet":
        setSelectedOption("WalletSuccess");
        break;
      case "WalletSuccess":
        setSelectedOption("OrderCard");
        logScreenView("order_card");
        break;
      default:
        break;
    }
  };

  const handleClose = () => {};

  const onOrgSuccess = (orgId: string) => {
    switchForm();
    searchParams.set(ORG_ID, orgId);
    setSearchParams(searchParams);
    if (!userInfo || !cognitoRefreshToken) return;
    dispatch(
      switchOrganisationAction(
        {
          organisationId: orgId,
          userId: userInfo.id
        },
        () => {
          refreshSession(cognitoRefreshToken);
        }
      )
    );
  };

  const handleAddUser = () => {
    if (subscriptionLimits?.cashLimit === 0 && subscriptionLimits?.gneralLimit === 0) {
      setOnBoarding(false);
      setCompleteOnBoarding(true);
    } else {
      if (subscriptionLimits?.gneralLimit > 0) {
        handleNextClick();
        switchForm();
      } else if (subscriptionLimits?.cashLimit > 0) {
        dispatch(
          addWalletAction(
            {
              description: "A simple cash wallet for spending money",
              name: "Cash Wallet",
              userId: String(userDetail?.user?.id),
              walletType: "CASH"
            },
            () => {
              setOnBoarding(false);
              setCompleteOnBoarding(true);
            }
          )
        );
      }
    }
  };

  const handleAddWalletSuccess = () => {
    handleNextClick();
    switchForm();
  };

  const handleOrderCardSuccess = () => {
    setOnBoarding(false);
    setCompleteOnBoarding(true);
  };

  const handlePlanSelectSuccess = async (subsId: string) => {
    if (organisationType === REFERER) {
      setOnBoarding(false);
      setCompleteOnBoarding(true);
    } else {
      handleNextClick();
      switchForm();
      if (cognitoRefreshToken) {
        await refreshSession(cognitoRefreshToken);
        getSubscriptionDetail(subsId);
      }
    }
  };

  useEffect(() => {
    if (
      userInfo?.role &&
      cognitoConfig?.organisationSubscriptions &&
      cognitoConfig?.organisationSubscriptions?.length > 0 &&
      organisationId
    ) {
      getSubscriptionDetail(cognitoConfig?.organisationSubscriptions[0]);
    }
  }, [cognitoConfig?.organisationSubscriptions, userInfo?.role]);

  const handleOptionClick = (option: string) => {
    logUserInteraction("onboarding_option_click", { option });
    setOpen(false);
    setOnBoarding(true);
    setSelectedOption(option);
    logScreenView("add_organization");

    let selectedTimelineSteps;

    switch (option) {
      case "createParticipantOrganization":
        searchParams.set(ORG_TYPE, INDIVIDUAL);
        selectedTimelineSteps = IndividualTimelineSteps;
        break;
      case "createReferrerOrganization":
        searchParams.set(ORG_TYPE, REFERER);
        selectedTimelineSteps = RefererTimelineSteps;
        break;
      case "createOrganization":
      default:
        searchParams.set(ORG_TYPE, CORPORATE);
        selectedTimelineSteps = CorporateTimelineSteps;
        break;
    }
    setSearchParams(searchParams);
    setTimelineSteps(selectedTimelineSteps);
  };

  const renderForm = () => {
    switch (selectedOption) {
      case "createOrganization":
        return (
          <AddOrgModal
            handleClose={handleClose}
            onSuccess={onOrgSuccess}
            rootContainrOver={styles.addOrgOver}
            closeButton={false}
            orgLoading={onboardingLoading}
            buttonText="Next"
            Orgtype="CORPORATE"
          />
        );
      case "createParticipantOrganization":
        return (
          <AddOrgModal
            handleClose={handleClose}
            onSuccess={onOrgSuccess}
            rootContainrOver={styles.addOrgOver}
            closeButton={false}
            orgLoading={onboardingLoading}
            buttonText="Next"
            forParticipant
            Orgtype={INDIVIDUAL}
            heading="Participant Details"
          />
        );
      case "createReferrerOrganization":
        return (
          <AddOrgModal
            handleClose={handleClose}
            onSuccess={onOrgSuccess}
            rootContainrOver={styles.addOrgOver}
            closeButton={false}
            orgLoading={onboardingLoading}
            buttonText="Next"
            forParticipant
            Orgtype="REFERER"
            heading="Referer Details"
          />
        );
      case "OrganisationSuccess":
        return (
          <OnBoardingSuccess
            secondaryButton={false}
            onNext={() => {
              switchForm();
              handleNextClick();
            }}
            onBack={() => {
              navigate("/");
            }}
            heading="You've created your Account!"
            subHeading="The next step is to select a subscription that works best for your account."
            buttonText={{ primaryButton: "Select Subscription" }}
            image={OrganisationCreated}
          />
        );
      case "SubscriptionModal":
        return <SubscriptionModal onSuccess={handlePlanSelectSuccess} orgId={organisationId} />;
      case "SubscriptionSuccess":
        return (
          <OnBoardingSuccess
            onNext={() => {
              switchForm();
              handleNextClick();
            }}
            onBack={() => {
              navigate("/");
            }}
            heading="You've selected a Subscription!"
            subHeading={
              organisationType === INDIVIDUAL
                ? "Confirm your details to finalise your participant user creation"
                : "It's time to add your first participant so that you never have to send another monthly statements or collect a box of receipts! Everyone will have access through their app."
            }
            buttonText={{ primaryButton: organisationType === INDIVIDUAL ? "Confirm details" : "Add a Participant" }}
            image={OrganisationCreated}
          />
        );
      case "addParticipant":
        return (
          <AddUserModal
            onAddUserSuccess={handleAddUser}
            rootContainer={styles.addParticipantOver}
            role={PARTICIPANT}
            buttonText={{ primaryButton: "Next" }}
            userloading={userLoading}
            heading={organisationType === INDIVIDUAL ? "Confirm Participant Detail" : undefined}
          />
        );

      case "ParticpantSuccess":
        return (
          <OnBoardingSuccess
            onNext={() => {
              switchForm();
              handleNextClick();
            }}
            onBack={() => {
              navigate("/");
            }}
            heading="Your participant needs a wallet!"
            subHeading="Give your participant their first wallet so that they can keep their money there. The wallet allows you to deposit, withdraw and spend while keeping all of the evidence."
            buttonText={{ primaryButton: "Add a Wallet" }}
            image={ParticipantSuccess}
          />
        );

      case "addWallet":
        return (
          <AddWalletModal
            handleClose={handleClose}
            onAddWalletSuccess={handleAddWalletSuccess}
            rootContainer={styles.addWalletOver}
            closeButton={false}
            buttonText={{ primaryButton: "Create 1 Wallet" }}
            userId={String(userDetail?.user?.id)}
            walletloading={walletLoading}
            generalLimit={subscriptionLimits?.gneralLimit}
          />
        );

      case "WalletSuccess":
        return (
          <OnBoardingSuccess
            onNext={() => {
              switchForm();
              handleNextClick();
            }}
            onBack={() => {
              navigate("/");
            }}
            heading="You're almost done!"
            subHeading="You need cards so that you can pay! Order a card for your participant that either they or the support workers will use."
            buttonText={{ primaryButton: "Order Card" }}
            image={WalletSuccess}
          />
        );
      case "OrderCard":
        return <OrderCardModal onSuccess={handleOrderCardSuccess} userData={userDetail} />;

      default:
        return null;
    }
  };

  useEffect(() => {
    if (nextClickCounter > 0) {
      handleNextClick();
      setNextClickCounter(nextClickCounter - 1);
    }
  }, [nextClickCounter]);

  const onBoardComplete = async () => {
    if (!cognitoRefreshToken) return;
    await refreshSession(cognitoRefreshToken);
    navigate("/");
  };

  return (
    <div className={styles.onboarding}>
      {open && (
        <Modalui
          open={open}
          handleClose={handleClose}
          modaluiOver={styles.modaluiOver}
          modalShadowOver={styles.modalShadowOver}
          backgroundColor={"#00000000"}
        >
          <div className={styles.popUpLayout}>
            <div className={styles.logo}>
              <div className={styles.logoL}>{spendAbleLogo}</div>
              <div className={styles.logoR}>
                <h1 className={styles.title}>SpendAble</h1>
                <div className={styles.subTitle}>Enabling Choices, Empowering Change</div>
              </div>
            </div>
            <p className={styles.textWrapper}>Which one best describes you?</p>
            <div className={styles.selectButtons}>
              <div
                className={styles.divWrapper}
                onClick={() => {
                  handleOptionClick("createParticipantOrganization");
                }}
              >
                <div className={styles.div}>
                  <div className={styles.rectangle}>
                    <div className={styles.textWrapper2}>I'm setting SpendAble for an individual participant</div>
                    <img src={individual} alt="individual" />
                  </div>
                </div>
              </div>
              <div
                className={styles.divWrapper}
                onClick={() => {
                  handleOptionClick("createOrganization");
                }}
              >
                <div className={styles.div}>
                  <div className={styles.rectangle}>
                    <p className={styles.textWrapper2}>I'm setting SpendAble for a service provider</p>
                    <img src={corporate} alt="individual" />
                  </div>
                </div>
              </div>
              <div
                className={styles.divWrapper}
                onClick={() => {
                  handleOptionClick("createReferrerOrganization");
                }}
              >
                <div className={styles.div}>
                  <div className={styles.rectangle}>
                    <p className={styles.textWrapper2}>I&apos;m referring multiple participants to SpendAble</p>
                    <img src={referral} alt="referral" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modalui>
      )}
      {onBoarding && (
        <div className={styles.onBoardingLayout}>
          <div className={styles.onBoardingNav}>
            <div className={styles.navContainer}>
              <div
                className={` ${styles.navBottomLogo} ${styles.sideOpt}`}
                style={{ cursor: userInfo?.role ? "pointer" : "default" }}
                onClick={() => {
                  if (userInfo?.role) {
                    navigate("/");
                  }
                }}
              >
                <img src={poweredBy} alt="logo" />
              </div>
              <div className={styles.onBoardingNavText}>Onboarding Progress</div>
              <div className={styles.verticalStpeer}>
                <VerticalTimeline
                  TimelineSteps={timelineSteps}
                  currentNode={currentNode}
                  currentSubsection={currentSubsection}
                  fillLevels={fillLevels}
                  lastNodeText={organisationType === "REFERER" ? "Select Subscription" : undefined}
                />
              </div>
              <div className={styles.navigButton}>
                <Tooltip title="Sign Out">
                  <div
                    className={styles.logoutBtn}
                    onClick={() => {
                      dispatch(logout());
                      navigate("/sign-in");
                    }}
                  >
                    <ExitToAppOutlinedIcon />
                  </div>
                </Tooltip>
                {userInfo?.role && (
                  <Tooltip title="Dashboard">
                    <div
                      className={styles.homeBtn}
                      onClick={() => {
                        navigate("/");
                      }}
                    >
                      <HomeOutlinedIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
          <div className={styles.onBoardingContent}>
            <div className={styles.onBoardingForm}>{renderForm()}</div>
          </div>
        </div>
      )}
      {completeOnBoarding && (
        <Modalui
          open={completeOnBoarding}
          handleClose={handleClose}
          modaluiOver={styles.modaluiOver}
          modalShadowOver={styles.modalShadowOver}
          backgroundColor={"#00000000"}
        >
          <OnBoardingSuccess
            onNext={onBoardComplete}
            onBack={onBoardComplete}
            heading="Your Onboarding is Complete!"
            subHeading="You have successfully onboarded a new Participant"
            buttonText={{ primaryButton: "Go to Dashboard" }}
            image={OnBoardComplete}
            secondaryButton={false}
          />
        </Modalui>
      )}
    </div>
  );
}
